import React, { useEffect, useState, useRef } from 'react';
import { Modal } from './Modal';
import { ProductForm } from './ProductForm';
import { BulkPushModal } from './BulkPushModal';
import { Plus, CheckCircle, AlertTriangle, RefreshCw, Trash2, CheckSquare } from 'lucide-react';
import { addOrUpdateProduct, getProducts, updateProductStatus, deleteProduct, addBulkProduct } from '../lib/db';
import { SearchBar } from './SearchBar';
import { ViewToggle } from './ViewToggle';
import { ProductList } from './ProductList';
import { EditProductModal } from './EditProductModal';
import { ConfirmationModal } from './ConfirmationModal';
import { PullToRefresh } from './PullToRefresh';
import { sendToWooCommerce, startBulkSync, resumeSyncSession, checkForPendingSync, cancelBulkSync } from '../lib/woocommerce';
import { saveCsvBackup, saveCsvBackupServer } from '../lib/csvBackups';
import { subscribeToProducts } from '../lib/supabase';
import { SyncProgressModal } from './SyncProgressModal';
import toast from 'react-hot-toast';
import Confetti from 'react-confetti';

export function Dashboard() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkPushOpen, setIsBulkPushOpen] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(true);
  const [validatingAll, setValidatingAll] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [syncId, setSyncId] = useState<string | null>(null);
  const [pendingSync, setPendingSync] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showCartTotal, setShowCartTotal] = useState(false);
  const [syncProgress, setSyncProgress] = useState({
    show: false,
    total: 0,
    current: 0,
    currentProduct: null,
    status: 'syncing' as 'syncing' | 'completed' | 'error' | 'cancelled',
    error: ''
  });

  const [confirmAction, setConfirmAction] = useState<{
    show: boolean;
    type: 'complete' | 'delete' | 'woocommerce' | 'validate-all' | 'resume-sync' | 'delete-selected';
    productId: number | null;
    title: string;
    message: string;
    product?: any;
    syncId?: string;
  }>({
    show: false,
    type: 'complete',
    productId: null,
    title: '',
    message: ''
  });

  // Paniers déjà envoyés vers WooCommerce dans cette session : on les masque même si
  // la base (délai de cohérence / abonnement temps réel) les renvoie encore en "draft".
  const recentlySentRef = useRef<Set<number>>(new Set());

  const loadProducts = async () => {
    try {
      setLoading(true);
      console.log('Loading draft products...');
      const data = await getProducts('draft');
      console.log('Loaded products:', data);
      // Filtrer les paniers déjà envoyés pour éviter qu'ils réapparaissent
      const filtered = (data || []).filter((p: any) => !recentlySentRef.current.has(p.id));
      setProducts(filtered);
      setSelectedProducts([]);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const checkPendingSynchronization = async () => {
    try {
      const pendingSync = await checkForPendingSync();
      if (pendingSync && pendingSync.pendingCount > 0) {
        setPendingSync(pendingSync);
        toast.custom((t) => (
          <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <AlertTriangle className="h-10 w-10 text-yellow-500" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Synchronisation interrompue
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {pendingSync.pendingCount} produit{pendingSync.pendingCount > 1 ? 's' : ''} en attente de synchronisation. Voulez-vous reprendre?
                  </p>
                  <div className="mt-2 flex">
                    <button
                      onClick={() => {
                        toast.dismiss(t.id);
                        showConfirmResumeSync(pendingSync.session.id);
                      }}
                      className="bg-blue-500 text-white px-3 py-1 rounded-md text-sm font-medium"
                    >
                      Reprendre
                    </button>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="ml-3 bg-gray-200 px-3 py-1 rounded-md text-sm font-medium"
                    >
                      Ignorer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ), { duration: 10000 });
      } else {
        setPendingSync(null);
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des synchronisations en attente:', error);
    }
  };

  useEffect(() => {
    loadProducts();
    checkPendingSynchronization();
    const unsubscribe = subscribeToProducts(() => {
      loadProducts();
      checkPendingSynchronization();
    });
    return () => {
      unsubscribe.unsubscribe();
    };
  }, []);

  const handleSubmit = async (data: any) => {
    await addOrUpdateProduct(data);
    await loadProducts();
    setIsModalOpen(false);
  };

  const handleBulkSubmit = async (data: {
    clientNumbers: string[];
    category: string;
    note: string;
    price: string;
  }) => {
    await addBulkProduct(data);
    await loadProducts();
    setIsBulkPushOpen(false);
  };

  const handleDelete = (id: number) => {
    setConfirmAction({
      show: true,
      type: 'delete',
      productId: id,
      title: 'Confirmer la suppression',
      message: 'Voulez-vous vraiment supprimer ce produit ?'
    });
  };

  const showConfirmComplete = (id: number) => {
    setConfirmAction({
      show: true,
      type: 'complete',
      productId: id,
      title: 'Déplacer vers la liste des commandes',
      message: 'Voulez-vous vraiment déplacer ce produit vers la liste des commandes ?'
    });
  };

  const showConfirmWooCommerce = (product: any) => {
    setConfirmAction({
      show: true,
      type: 'woocommerce',
      productId: product.id,
      product: product,
      title: 'Envoyer vers WooCommerce',
      message: 'Voulez-vous vraiment envoyer ce produit vers WooCommerce ?'
    });
  };

  const showConfirmValidateAll = () => {
    setConfirmAction({
      show: true,
      type: 'validate-all',
      productId: null,
      title: 'Valider tous les produits',
      message: 'Voulez-vous vraiment envoyer tous les produits vers WooCommerce ?'
    });
  };

  const showConfirmResumeSync = (syncId: string) => {
    setConfirmAction({
      show: true,
      type: 'resume-sync',
      productId: null,
      syncId: syncId,
      title: 'Reprendre la synchronisation',
      message: 'Voulez-vous reprendre la synchronisation interrompue ?'
    });
  };

  const showConfirmDeleteSelected = () => {
    if (selectedProducts.length === 0) {
      toast.error('Aucun produit sélectionné');
      return;
    }

    setConfirmAction({
      show: true,
      type: 'delete-selected',
      productId: null,
      title: 'Supprimer les produits sélectionnés',
      message: `Voulez-vous vraiment supprimer les ${selectedProducts.length} produits sélectionnés ?`
    });
  };

  const handleConfirmAction = async () => {
    try {
      switch (confirmAction.type) {
        case 'complete':
          if (!confirmAction.productId) return;
          await updateProductStatus(confirmAction.productId, 'pending');
          toast.success('Produit déplacé vers la liste des commandes');
          break;
          
        case 'delete':
          if (!confirmAction.productId) return;
          await deleteProduct(confirmAction.productId);
          break;
          
        case 'delete-selected':
          if (selectedProducts.length === 0) return;
          
          // Supprimer les produits sélectionnés un par un
          for (const productId of selectedProducts) {
            await deleteProduct(productId);
          }
          
          toast.success(`${selectedProducts.length} produits supprimés avec succès`);
          setSelectedProducts([]);
          break;
          
        case 'woocommerce':
          if (confirmAction.product) {
            const pid = confirmAction.productId!;
            const sendingToast = toast.loading('Envoi vers WooCommerce...');

            // Masquer le panier IMMÉDIATEMENT (avant l'envoi) et le mémoriser, pour qu'aucune
            // relecture (loadProducts final OU abonnement temps réel) ne puisse le ré-afficher
            // pendant le délai de cohérence de la base.
            recentlySentRef.current.add(pid);
            setProducts(prev => prev.filter(p => p.id !== pid));
            setSelectedProducts(prev => prev.filter(id => id !== pid));

            try {
              await sendToWooCommerce(confirmAction.product);
              toast.dismiss(sendingToast);

              // Célébration comme "Valider tout"
              setShowConfetti(true);
              toast.success('Panier envoyé vers WooCommerce ! Commande prête 🎉', {
                duration: 4000,
                icon: '🎉'
              });
              setTimeout(() => setShowConfetti(false), 4000);

              // Succès : on sort sans relire (le panier reste masqué)
              return;
            } catch (error: any) {
              // Échec réel : le produit n'est pas parti → on ré-affiche le panier
              recentlySentRef.current.delete(pid);
              toast.dismiss(sendingToast);
              console.error('Erreur envoi WooCommerce:', error);
              toast.error(error.response?.data?.message || error.message || 'Erreur lors de l\'envoi vers WooCommerce', {
                duration: 5000
              });
            }
          }
          break;
          
        case 'validate-all':
          // Sécurité : enregistrer un backup CSV des paniers avant tout envoi.
          // En priorité en ligne (dossier /csv/), avec repli local si le serveur est injoignable.
          try {
            await saveCsvBackupServer(products);
            toast.success(`Backup CSV de ${products.length} panier(s) enregistré en ligne (voir Connexion)`, { duration: 3000 });
          } catch (csvError) {
            console.error('Erreur backup CSV en ligne, repli local:', csvError);
            try {
              saveCsvBackup(products);
              toast('Backup CSV enregistré localement (serveur injoignable)', { duration: 4000, icon: '⚠️' });
            } catch (localError) {
              console.error('Erreur backup CSV local:', localError);
            }
          }

          setSyncProgress({
            show: true,
            total: products.length,
            current: 0,
            currentProduct: null,
            status: 'syncing',
            error: ''
          });
          
          try {
            const result = await startBulkSync(products, (progress) => {
              setSyncProgress(prev => ({
                ...prev,
                current: progress.current,
                currentProduct: progress.currentProduct
              }));
            });
            
            setSyncId(result.syncId);

            if (result.cancelled) {
              setSyncProgress(prev => ({
                ...prev,
                current: result.syncedProducts,
                status: 'cancelled',
                error: `Synchronisation annulée. ${result.syncedProducts}/${result.totalProducts} envoyés.`
              }));
              await checkPendingSynchronization();
            } else if (result.failedProducts > 0) {
              const errorDetails = result.failedDetails
                ?.slice(0, 3)
                .map((d: any) => `#${d.clientNumber}: ${d.error}`)
                .join('\n') || '';
              const more = result.failedProducts > 3 ? `\n...et ${result.failedProducts - 3} autres` : '';

              setSyncProgress(prev => ({
                ...prev,
                current: result.syncedProducts,
                status: 'error',
                error: `${result.syncedProducts}/${result.totalProducts} envoyés. ${result.failedProducts} échec(s):\n${errorDetails}${more}`
              }));
              toast.error(`${result.failedProducts} panier(s) non envoyé(s)`, { duration: 5000 });
            } else if (result.syncedProducts === 0) {
              setSyncProgress(prev => ({
                ...prev,
                current: 0,
                status: 'error',
                error: 'Aucun produit n\'a été envoyé. Vérifiez la configuration WooCommerce.'
              }));
              toast.error('Aucun panier envoyé', { duration: 5000 });
            } else {
              setSyncProgress(prev => ({
                ...prev,
                current: result.syncedProducts,
                status: 'completed'
              }));

              setShowConfetti(true);
              toast.success('Félicitations pour votre LIVE, vos commandes sont désormais prêtes !', {
                duration: 5000,
                icon: '🎉'
              });

              setTimeout(() => {
                setShowConfetti(false);
              }, 5000);

              // La session est terminée : retirer le bouton "Reprendre sync"
              setPendingSync(null);
            }
            
          } catch (error: any) {
            console.error('Erreur lors de la validation en masse:', error);
            setSyncProgress(prev => ({
              ...prev,
              status: 'error',
              error: error.message || 'Une erreur est survenue lors de l\'envoi des produits'
            }));
          }
          break;
          
        case 'resume-sync':
          if (!confirmAction.syncId) return;
          
          setSyncProgress({
            show: true,
            total: pendingSync?.pendingCount || 0,
            current: 0,
            currentProduct: null,
            status: 'syncing',
            error: ''
          });
          
          try {
            const result = await resumeSyncSession(confirmAction.syncId, (progress) => {
              setSyncProgress(prev => ({
                ...prev,
                current: progress.current,
                currentProduct: progress.currentProduct
              }));
            });
            
            setSyncId(result.syncId);
            setSyncProgress(prev => ({
              ...prev,
              current: prev.total,
              status: 'completed'
            }));

            // La synchronisation reprise est terminée : retirer le bouton "Reprendre sync"
            setPendingSync(null);
            await checkPendingSynchronization();

            // Effet de célébration comme pour "Valider tout"
            setShowConfetti(true);
            toast.success('Synchronisation reprise et terminée !', {
              duration: 5000,
              icon: '🎉'
            });
            setTimeout(() => {
              setShowConfetti(false);
            }, 5000);

          } catch (error: any) {
            console.error('Erreur lors de la reprise de la synchronisation:', error);
            setSyncProgress(prev => ({
              ...prev,
              status: 'error',
              error: error.message || 'Une erreur est survenue lors de la reprise de la synchronisation'
            }));
          }
          break;
      }
      await loadProducts();
    } catch (error) {
      console.error('Une erreur est survenue:', error);
      toast.error('Une erreur est survenue lors de l\'envoi des produits');
    }
  };

  const handleCancelSync = () => {
    // Demander l'arrêt réel de la boucle de synchronisation en cours
    cancelBulkSync();
    setSyncProgress(prev => ({
      ...prev,
      status: 'cancelled'
    }));
    // Rafraîchir l'état des synchronisations en attente (une reprise reste possible)
    checkPendingSynchronization();
  };

  // Gérer la sélection de tous les produits
  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      // Si tous sont déjà sélectionnés, on désélectionne tout
      setSelectedProducts([]);
    } else {
      // Sinon, on sélectionne tous les produits
      setSelectedProducts(products.map(p => p.id));
    }
  };

  // Gérer la sélection d'un produit individuel
  const handleSelectProduct = (productId: number, selected: boolean) => {
    if (selected) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  return (
    <PullToRefresh onRefresh={loadProducts}>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
        />
      )}
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold text-gray-900">Live</h2>
                <button
                  type="button"
                  onClick={() => setShowCartTotal(prev => !prev)}
                  className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
                  title="Cliquer pour afficher le total en €"
                >
                  {products.length} panier{products.length > 1 ? 's' : ''} ouvert{products.length > 1 ? 's' : ''}
                  {showCartTotal && (
                    <span className="ml-2 font-bold">
                      · {products.reduce((sum, p) => sum + (Number(p.total_price) || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                  )}
                </button>
              </div>
              <div className="flex space-x-2 sm:space-x-4">
                {/* View toggle buttons - hide on smallest screens (mobile mode) */}
                <div className="hidden sm:block">
                  <ViewToggle view={view} onViewChange={setView} />
                </div>
                
                {/* Show Pending Sync button */}
                {pendingSync && pendingSync.pendingCount > 0 && (
                  <button
                    onClick={() => showConfirmResumeSync(pendingSync.session.id)}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-yellow-600"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span className="hidden sm:inline">Reprendre sync ({pendingSync.pendingCount})</span>
                  </button>
                )}
                
                {/* Desktop buttons - hide on mobile */}
                <div className="hidden sm:flex space-x-2 sm:space-x-4">
                  {(selectedProducts.length > 0 || products.length > 0) && (
                    <button
                      onClick={handleSelectAll}
                      className="bg-gray-500 text-white px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-gray-600"
                    >
                      <CheckSquare className="w-4 h-4" />
                      <span className="hidden sm:inline">
                        {selectedProducts.length === products.length ? 'Désélectionner tout' : 'Sélectionner tout'}
                      </span>
                    </button>
                  )}
                  
                  {selectedProducts.length > 0 && (
                    <button
                      onClick={showConfirmDeleteSelected}
                      className="bg-red-500 text-white px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Supprimer ({selectedProducts.length})</span>
                    </button>
                  )}
                  
                  {products.length > 0 && (
                    <button
                      onClick={showConfirmValidateAll}
                      className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-green-600"
                      disabled={validatingAll}
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span className="hidden sm:inline">Valider tout</span>
                    </button>
                  )}
                  
                  {/* Add new product button */}
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-blue-600"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Push</span>
                  </button>

                  {/* Bulk Push button */}
                  <button
                    onClick={() => setIsBulkPushOpen(true)}
                    className="bg-purple-500 text-white px-4 py-2 rounded-md flex items-center space-x-2 hover:bg-purple-600"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Bulk Push</span>
                  </button>
                </div>
                
                {/* Mobile-only toggle button, visible only on smallest screens */}
                <div className="sm:hidden">
                  <ViewToggle view={view} onViewChange={setView} />
                </div>
              </div>
            </div>
            
            {/* Searching */}
            <div className="mt-4 relative">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Recherche par numéro client"
              />
              
              {/* Mobile-only action buttons BELOW search bar */}
              <div className="sm:hidden mt-4 grid grid-cols-2 gap-2">
                {/* Select All button appears when products are selected or available */}
                {selectedProducts.length > 0 && (
                  <button
                    onClick={handleSelectAll}
                    className="col-span-1 bg-gray-500 text-white px-2 py-3 rounded-md flex items-center justify-center space-x-1 hover:bg-gray-600"
                  >
                    <CheckSquare className="w-4 h-4" />
                    <span className="text-sm">{selectedProducts.length === products.length ? 'Désélect. tout' : 'Sélect. tout'}</span>
                  </button>
                )}
                
                {selectedProducts.length > 0 && (
                  <button
                    onClick={showConfirmDeleteSelected}
                    className="col-span-1 bg-red-500 text-white px-2 py-2 rounded-md flex items-center justify-center space-x-1 hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">Supprimer ({selectedProducts.length})</span>
                  </button>
                )}
                
                {/* Always show these buttons if no products are selected */}
                {!selectedProducts.length && (
                  <>
                    {/* Validate All button only if there are products */}
                    {products.length > 0 && (
                      <button
                        onClick={showConfirmValidateAll}
                        className="bg-green-500 text-white px-2 py-3 rounded-md flex items-center justify-center space-x-1 hover:bg-green-600"
                        disabled={validatingAll}
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">Valider tout</span>
                      </button>
                    )}
                    
                    {/* Push button always visible */}
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="bg-blue-500 text-white px-2 py-3 rounded-md flex items-center justify-center space-x-1 hover:bg-blue-600"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm">Push</span>
                    </button>

                    {/* Bulk Push button */}
                    <button
                      onClick={() => setIsBulkPushOpen(true)}
                      className={`bg-purple-500 text-white px-2 py-3 rounded-md flex items-center justify-center space-x-1 hover:bg-purple-600 ${products.length === 0 ? 'col-span-2' : ''}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm">Bulk</span>
                    </button>
                  </>
                )}
                
                {/* If no products are available but products are selected, show Push button */}
                {selectedProducts.length > 0 && selectedProducts.length < products.length && (
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="col-span-2 mt-2 bg-blue-500 text-white px-2 py-3 rounded-md flex items-center justify-center space-x-1 hover:bg-blue-600"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">Push</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Aucun produit en cours
          </div>
        ) : (
          <ProductList
            products={products}
            view={view}
            searchTerm={searchTerm}
            onEdit={setEditingProduct}
            onStatusChange={showConfirmComplete}
            onDelete={handleDelete}
            onWooCommerce={showConfirmWooCommerce}
            actionIcon="complete"
            selectedProducts={selectedProducts}
            onSelectProduct={handleSelectProduct}
          />
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Ajouter un produit"
        >
          <ProductForm
            onSubmit={handleSubmit}
            onClose={() => setIsModalOpen(false)}
          />
        </Modal>

        <Modal
          isOpen={isBulkPushOpen}
          onClose={() => setIsBulkPushOpen(false)}
          title="Ajouter un produit à plusieurs clients"
        >
          <BulkPushModal
            onSubmit={handleBulkSubmit}
            onClose={() => setIsBulkPushOpen(false)}
          />
        </Modal>

        {editingProduct && (
          <EditProductModal
            product={editingProduct}
            isOpen={true}
            onClose={() => setEditingProduct(null)}
            onUpdate={loadProducts}
            showStatusChange={true}
            currentStatus="draft"
            onStatusChange={(id) => {
              updateProductStatus(id, 'pending');
              loadProducts();
              setEditingProduct(null);
            }}
          />
        )}

        <ConfirmationModal
          isOpen={confirmAction.show}
          onClose={() => setConfirmAction({ ...confirmAction, show: false })}
          onConfirm={handleConfirmAction}
          title={confirmAction.title}
          message={confirmAction.message}
        />

        <SyncProgressModal
          isOpen={syncProgress.show}
          onClose={() => setSyncProgress(prev => ({ ...prev, show: false }))}
          onCancel={handleCancelSync}
          total={syncProgress.total}
          current={syncProgress.current}
          currentProduct={syncProgress.currentProduct}
          status={syncProgress.status}
          error={syncProgress.error}
        />
      </div>
    </PullToRefresh>
  );
}